import axios from 'axios';

/**
 * Theme resolution, application and persistence.
 *
 * THIS FILE IS THE SEAM. Everything about *how* a preference is stored lives here --
 * components and the Pinia store never touch storage. Swapping the server column for a
 * different one, or dropping to browser-only, is a change to this file plus the Core
 * command, and nothing else.
 */

export const THEME_PREFERENCES = ['light', 'dark', 'system'];

// Written before the bundle loads by the inline script in public/index.html. Deliberately
// NOT per-user: at first paint no user is known yet.
const LAST_APPLIED_KEY = 'mosaic.theme.lastApplied';
const perUserKey = userId => `mosaic.theme.${userId}`;

export function isAllowedPreference(preference) {
    return THEME_PREFERENCES.includes(preference);
}

/** Pure: which theme actually applies, given a preference and the OS setting. */
export function resolveTheme(preference, systemPrefersDark) {
    if (preference === 'dark') return 'dark';
    if (preference === 'light') return 'light';
    if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
    return 'light';
}

export function systemPrefersDark() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// True between beforeprint and afterprint. Module-scoped because applyTheme and the print
// guard both need it and they have no other shared owner -- and because the alternative, a
// cached "restore this value later" variable inside the guard, went stale the moment anything
// else re-applied the theme during an open print dialog.
let printing = false;

/**
 * Stamp the resolved theme on <html>. The only place `data-theme` is written, and the only
 * place `data-bs-theme` is written OUTSIDE a print -- installPrintThemeGuard writes the
 * latter during one, which is why this function has to know a print is in progress.
 *
 * Deliberately does NOT touch localStorage. It used to, and that was a bug: the store
 * applies the theme optimistically BEFORE awaiting the save, so a failed POST still left
 * the pre-paint key holding a value the server never accepted -- and the next load painted
 * it, then reverted. Separating the two lets the caller decide when a value has earned the
 * right to be remembered. See rememberLastApplied.
 */
export function applyTheme(resolved) {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    // While a print is in progress Bootstrap's attribute is held at 'light' -- see
    // installPrintThemeGuard. Without this check, anything that re-applies the theme during
    // an open print dialog (an OS flip under 'system', or hydrate()'s trailing reconcile)
    // would silently re-stamp 'dark' and bring Bootstrap's dark rules back mid-print.
    root.setAttribute('data-bs-theme', printing ? 'light' : resolved);
}

/**
 * Record the theme the next page load should paint before the bundle boots -- this is what
 * prevents the white flash. Call it only for values that are AUTHORITATIVE: saved to the
 * server, read back from the server or the cache, or an OS change under 'system'.
 *
 * Not per-user, and cannot be: the inline script in index.html reads it before we know who
 * is signing in. hydrate() is what corrects it once the user is known.
 */
export function rememberLastApplied(resolved) {
    try {
        localStorage.setItem(LAST_APPLIED_KEY, resolved);
    } catch {
        // Private browsing / storage disabled -- the app still works, it just flashes on load.
    }
}

export function readCachedPreference(userId) {
    try {
        return localStorage.getItem(perUserKey(userId));
    } catch {
        return null;
    }
}

export function writeCachedPreference(userId, preference) {
    try {
        localStorage.setItem(perUserKey(userId), preference);
    } catch {
        // See applyTheme.
    }
}

// suppressGlobalError on both: these are best-effort. The global response interceptor in
// main.js otherwise raises an error toast that does NOT auto-dismiss, and on a 403 pushes
// the user to the Unauthorized page -- during app boot, over an appearance setting. The
// callers already fall back to the cached preference, so a failure here gives the user
// nothing to act on and nothing worth interrupting them for.
export async function fetchPreference() {
    const response = await axios.get('users/uipreferences', { suppressGlobalError: true });
    return response.data?.theme ?? 'light';
}

export async function savePreference(preference) {
    await axios.post('users/uipreferences', { theme: preference }, { suppressGlobalError: true });
}

/**
 * Make printing light by holding Bootstrap's theme attribute at 'light' for the print,
 * then putting it back.
 *
 * WHY THIS IS NOT DONE IN CSS. Our own dark layer is wrapped in @media screen, so it cannot
 * reach print at all. Bootstrap 5.3's dark mode cannot be handled the same way: it ships in
 * chunk-vendors.css, where we cannot wrap it, and four of its rules declare their custom
 * properties ON THE COMPONENT rather than on :root --
 *
 *   [data-bs-theme=dark] .form-select            { --bs-form-select-bg-img: <#dee2e6 chevron> }
 *   [data-bs-theme=dark] .form-switch .form-check-input:not(:checked):not(:focus)
 *   [data-bs-theme=dark] .navbar-toggler-icon
 *   [data-bs-theme=dark] .accordion-button:after
 *
 * A custom property declared on the element itself always beats one inherited from <html>, so
 * an `html[data-bs-theme=dark] { --bs-*: ... }` reset in @media print can NEVER win against
 * those. An earlier version of this work claimed the CSS reset was "closed over a fixed set of
 * third-party names". It was not, and could not be: covering them in CSS means re-stating every
 * such rule at its own selector depth, on every Bootstrap upgrade.
 *
 * Holding the attribute at light instead makes every `[data-bs-theme=dark]` rule stop matching at once,
 * at any depth, with nothing to enumerate and nothing to maintain.
 *
 * Returns an unsubscribe function, for symmetry with watchSystemTheme and for tests.
 */
export function installPrintThemeGuard() {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return () => {};
    }
    const root = document.documentElement;

    // No cached "restore to this" value, deliberately. An earlier version kept one, and it
    // went stale: anything re-applying the theme during an open print dialog (an OS flip
    // under 'system', hydrate()'s trailing reconcile) changed what the correct restore value
    // was, and afterprint then wrote the old one back -- leaving data-theme and data-bs-theme
    // disagreeing until the next reload.
    //
    // Instead the restore value is DERIVED from data-theme, which applyTheme keeps accurate
    // throughout the print because it is print-aware. The two attributes cannot drift.
    // One-shot "the user is back on the page" listeners, armed at beforeprint and removed
    // the moment the print ends by any route. This is the recovery that actually works.
    //
    // matchMedia('print') (below) rides the SAME print lifecycle as afterprint, so in the
    // scenarios where the latch genuinely sticks -- afterprint never arriving -- it cannot
    // fire either. It is kept because it is free and covers a browser that implements one
    // and not the other, but it is not the safety net it looked like.
    //
    // Interaction is independent of that lifecycle: if someone is clicking or typing in the
    // page, no print dialog is in front of it. Worst case the latch clears a fraction early
    // and data-bs-theme returns to dark while a print is still rendering, which costs a
    // dark-themed printout -- strictly better than a session stuck half-light with no way
    // back but a reload.
    const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'focus'];
    let interactionArmed = false;

    const disarmInteraction = () => {
        if (!interactionArmed) return;
        interactionArmed = false;
        INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, onInteraction, true));
    };
    function onInteraction() {
        if (printing) endPrint();
        else disarmInteraction();
    }
    function endPrint() {
        printing = false;
        disarmInteraction();
        root.setAttribute('data-bs-theme', root.getAttribute('data-theme') || 'light');
    }

    const onBeforePrint = () => {
        printing = true;
        root.setAttribute('data-bs-theme', 'light');
        if (!interactionArmed) {
            interactionArmed = true;
            // Capture phase so a stopPropagation() somewhere in the app cannot strand us.
            INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, onInteraction, true));
        }
    };
    const onAfterPrint = () => endPrint();

    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);

    // SECOND "print has ended" signal, because `printing` is a latch and a latch that only
    // one event can clear is a latch that can stick. A beforeprint with no matching
    // afterprint -- a cancelled dialog in some browsers, a navigation mid-print -- would
    // otherwise leave applyTheme convinced a print was in progress for the rest of the
    // session, writing data-bs-theme='light' against data-theme='dark' with no recovery
    // short of a reload. The previous implementation self-healed on the next applyTheme;
    // this one has to earn that back explicitly.
    //
    // matchMedia('print') is used as a recovery path only, never to SET the flag: browsers
    // disagree about whether print media matches while a dialog is open, and trusting it in
    // that direction would risk re-stamping dark mid-print, which is the bug this guard
    // exists to prevent.
    let unwatchPrintMedia = () => {};
    if (typeof window.matchMedia === 'function') {
        try {
            const printMedia = window.matchMedia('print');
            const onMediaChange = (event) => {
                if (!event.matches && printing) onAfterPrint();
            };
            if (typeof printMedia.addEventListener === 'function') {
                printMedia.addEventListener('change', onMediaChange);
                unwatchPrintMedia = () => printMedia.removeEventListener('change', onMediaChange);
            }
        } catch {
            // Older engines throw on an unsupported query. The events above still work.
        }
    }

    return () => {
        window.removeEventListener('beforeprint', onBeforePrint);
        window.removeEventListener('afterprint', onAfterPrint);
        unwatchPrintMedia();
        disarmInteraction();
        // Clear the flag too. Uninstalling mid-print would otherwise leave applyTheme
        // permanently convinced a print is in progress, pinning Bootstrap to light.
        printing = false;
    };
}

/**
 * Watch the OS setting. Returns an unsubscribe function; the store detaches this the
 * moment the user makes an explicit choice, so "system" never lingers.
 */
export function watchSystemTheme(onChange) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = event => onChange(event.matches);
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
}
